/**
 * One person. The header is who they are and how to reach them; the highlights are the five
 * things an agent needs in the first second of a call (are they a client, what is open, when did
 * we last speak); below, the facts, the story and the open work.
 */
import {
  ageOn,
  formatPhone,
  householdRoleLabel,
  languageLabel,
  leadSourceLabel,
  partyRoleLabel,
  policyStageLabel,
  stateLabel,
  toUsDate,
  type ContactRecord as Record,
} from "@tlcrm/shared";
import { useState } from "react";
import { Link, useParams } from "react-router";

import { useContact, useTasks } from "../../api/hooks";
import { useMe } from "../../auth/session";
import { History } from "../../components/History";
import { Icon } from "../../components/Icon";
import { TaskRows } from "../../components/TaskList";
import { Timeline } from "../../components/Timeline";
import {
  DncTag,
  ErrorState,
  LeadStatusMark,
  LifecyclePill,
  PostSaleMark,
  StageMark,
} from "../../components/ui";
import { NewPolicyModal } from "../../forms/NewPolicy";
import { NewTaskModal } from "../../forms/NewTask";
import { age, initials, money, moneyShort, when } from "../../lib/format";
import { dial } from "../../lib/dial";
import { useNow } from "../../lib/useNow";
import { useRecordKey } from "../../shell/useShortcuts";
import { LiveCallBar } from "../record/LiveCallBar";
import { ContactDetails } from "./ContactDetails";
import { PhonesCard } from "./PhonesCard";

export function ContactRecord() {
  const { id = "" } = useParams();
  const record = useContact(id);
  if (record.isError) {
    return (
      <div className="page">
        <ErrorState error={record.error} onRetry={() => void record.refetch()} />
      </div>
    );
  }
  if (record.isPending) {
    return (
      <div className="record-head" style={{ paddingBottom: "var(--s-8)" }}>
        <div className="skel" style={{ width: 280, height: 26, marginTop: "var(--s-6)" }} />
        <div className="skel" style={{ width: 420, height: 12, marginTop: "var(--s-5)" }} />
      </div>
    );
  }
  return <Loaded record={record.data} />;
}

function Loaded({ record }: { record: Record }) {
  const { contact, household, leads, policies } = record;
  const { can } = useMe();
  const now = useNow(1000);
  const [tab, setTab] = useState<"overview" | "history">("overview");
  const [newTask, setNewTask] = useState(false);
  const [newPolicy, setNewPolicy] = useState(false);
  const tasks = useTasks({ contactId: contact.id, assignee: "any" });

  const primary = contact.phones.find((p) => p.isPrimary) ?? contact.phones[0];
  const canDial = primary && !primary.onDnc && !contact.doNotCall && !primary.wrongNumber;
  const openLead = leads.find((l) => ["new", "working", "contacted"].includes(l.status));
  // In force: sold (Issued or Closed Won) and not cancelled or not taken.
  const active = policies.filter(
    (p) => (p.stage === "issued" || p.stage === "closed_won") && p.postSaleStatus === "active",
  );
  const cover = active
    .filter((p) => p.customerId === contact.id || p.roles?.includes("insured"))
    .reduce((sum, p) => sum + (p.totalCoverage ?? 0), 0);
  const pending = policies.find((p) => p.stage === "issued" && p.customerId === contact.id);

  useRecordKey({
    d: () => canDial && dial(primary.e164),
    n: () => document.getElementById(`note-${contact.id}`)?.focus(),
  });

  return (
    <>
      <LiveCallBar
        contactIds={[contact.id]}
        phones={contact.phones.map((p) => p.e164)}
        about={{ contactId: contact.id }}
        facts={
          <>
            {contact.dateOfBirth && (
              <span>
                DOB <b className="mono">{toUsDate(contact.dateOfBirth)}</b>
              </span>
            )}
            <span>
              Cover in force <b className="mono">{active.length ? moneyShort(cover) : "none"}</b>
            </span>
          </>
        }
      />
      <div className="record-head">
        <div className="record-crumb">
          <Link to="/contacts">Customers</Link>
          <Icon name="chevR" />
          <span>{contact.fullName}</span>
        </div>
        <div className="record-title">
          <span className="face face-customer">{initials(contact.fullName)}</span>
          <div style={{ minWidth: 0 }}>
            <h1>{contact.fullName}</h1>
            <div className="facts">
              <span className="id-mark id-customer">Customer</span>
              <LifecyclePill stage={contact.lifecycleStage} />
              {contact.doNotCall && <DncTag />}
              {contact.dateOfBirth && <span>Age {ageOn(contact.dateOfBirth)}</span>}
              {contact.state && <span>{stateLabel(contact.state)}</span>}
              <span>{languageLabel(contact.language)}</span>
              <span className="sep" />
              <span>Owner {contact.owner?.fullName ?? "unassigned"}</span>
            </div>
          </div>
          <div className="actions">
            <button className="btn" onClick={() => setNewTask(true)}>
              <Icon name="checkSquare" /> Task
            </button>
            {openLead ? (
              <Link className="btn" to={`/leads/${openLead.id}`}>
                <Icon name="inbox" /> Open lead
              </Link>
            ) : (
              <button className="btn" onClick={() => setNewPolicy(true)}>
                <Icon name="briefcase" /> New policy
              </button>
            )}
            {primary && (
              <button
                className="btn btn-primary"
                onClick={() => dial(primary.e164)}
                disabled={!canDial}
                title={canDial ? "Dial on the desk (D)" : "This number may not be called"}
              >
                <Icon name="phoneOut" /> Dial{" "}
                <span className="mono">{formatPhone(primary.e164)}</span>
              </button>
            )}
          </div>
        </div>

        <div className="highlights">
          <div>
            <span className="eyebrow">Cover in force</span>
            <div className="v mono">
              {active.length
                ? `${moneyShort(cover)} · ${active.length} ${active.length === 1 ? "policy" : "policies"}`
                : "None"}
            </div>
          </div>
          <div>
            <span className="eyebrow">Waiting on a draft</span>
            <div className="v">
              {pending ? (
                <Link to={`/policies/${pending.id}`}>
                  {policyStageLabel(pending.stage)} · {toUsDate(pending.dateSold)}
                </Link>
              ) : (
                "None"
              )}
            </div>
          </div>
          <div>
            <span className="eyebrow">Open lead</span>
            <div className="v">
              {openLead ? (
                <>
                  {leadSourceLabel(openLead.source)} ·{" "}
                  <span className="age">{age(now - new Date(openLead.receivedAt).getTime())}</span>
                </>
              ) : (
                "None"
              )}
            </div>
          </div>
          <div>
            <span className="eyebrow">Household</span>
            <div className="v">
              {household ? `${household.members.length} people` : "Not linked"}
            </div>
          </div>
          <div>
            <span className="eyebrow">Last updated</span>
            <div className="v">
              {when(contact.updatedAt)}
              {contact.updatedBy && (
                <span className="t3" style={{ fontWeight: 400 }}>
                  {" "}
                  · {contact.updatedBy.fullName}
                </span>
              )}
            </div>
          </div>
        </div>

        {can("audit:read") && (
          <div className="tabs" role="tablist">
            <button
              className="tab"
              role="tab"
              aria-selected={tab === "overview"}
              onClick={() => setTab("overview")}
            >
              Overview
            </button>
            <button
              className="tab"
              role="tab"
              aria-selected={tab === "history"}
              onClick={() => setTab("history")}
            >
              History
            </button>
          </div>
        )}
      </div>

      {tab === "history" ? (
        <div className="record-body two">
          <div className="stack">
            <ContactDetails contact={contact} />
          </div>
          <History entity="contact" id={contact.id} />
        </div>
      ) : (
        <div className="record-body">
          <div className="stack">
            <PhonesCard contact={contact} />
            <ContactDetails contact={contact} />
          </div>
          <div className="stack">
            <Timeline kind="contacts" id={contact.id} />
          </div>
          <div className="stack">
            <div className="card">
              <div className="card-head">
                <h3>Open tasks</h3>
                <button className="btn btn-sm" onClick={() => setNewTask(true)}>
                  Add
                </button>
              </div>
              {tasks.data?.length ? (
                <TaskRows tasks={tasks.data} now={now} showContact={false} />
              ) : (
                <div className="rel-empty">Nothing open.</div>
              )}
            </div>

            <div className="card">
              <div className="card-head">
                <h3>Policies</h3>
                <span className="count mono t3">{policies.length}</span>
              </div>
              <div className="rel">
                {policies.length === 0 && <div className="rel-empty">No policies on file.</div>}
                {policies.map((p) => (
                  <Link key={p.id} to={`/policies/${p.id}`} className="rel-item">
                    <div className="t">
                      {p.carrier ?? p.name}
                      <StageMark stage={p.stage} />
                      {p.postSaleStatus !== "active" && <PostSaleMark status={p.postSaleStatus} />}
                    </div>
                    <div className="s">
                      {p.product && <span>{p.product}</span>}
                      <span className="mono">{moneyShort(p.totalCoverage)}</span>
                      <span className="mono">{money(p.totalPremium, true)}/yr</span>
                      <span>Sold {toUsDate(p.dateSold)}</span>
                    </div>
                    <div className="s">
                      {p.customerId !== contact.id && (
                        <span className="tag">On {p.customerName}'s policy</span>
                      )}
                      {p.roles?.map((r) => (
                        <span key={r}>{partyRoleLabel(r)}</span>
                      ))}
                      <span>{p.owner.fullName}</span>
                    </div>
                  </Link>
                ))}
              </div>
            </div>

            {household && (
              <div className="card">
                <div className="card-head">
                  <h3>{household.name}</h3>
                </div>
                <div className="rel">
                  {household.members.map((m) => (
                    <Link
                      key={m.contactId}
                      to={`/contacts/${m.contactId}`}
                      className="rel-item"
                      aria-current={m.contactId === contact.id ? "page" : undefined}
                    >
                      <div className="t">
                        {m.fullName}
                        {m.contactId === contact.id && <span className="tag">This person</span>}
                      </div>
                      <div className="s">{m.role ? householdRoleLabel(m.role) : "Member"}</div>
                    </Link>
                  ))}
                </div>
              </div>
            )}

            <div className="card">
              <div className="card-head">
                <h3>Leads</h3>
              </div>
              <div className="rel">
                {leads.length === 0 && <div className="rel-empty">Never came in as a lead.</div>}
                {leads.map((l) => (
                  <Link key={l.id} to={`/leads/${l.id}`} className="rel-item">
                    <div className="t">
                      {l.vendor ?? leadSourceLabel(l.source)}
                      <LeadStatusMark status={l.status} />
                    </div>
                    <div className="s">
                      <span>{when(l.receivedAt)}</span>
                      {l.campaign && <span>{l.campaign}</span>}
                      <span>
                        {l.attempts} {l.attempts === 1 ? "try" : "tries"}
                      </span>
                      {l.firstContactedAt && (
                        <span>
                          reached in{" "}
                          <span className="mono">
                            {age(
                              new Date(l.firstContactedAt).getTime() -
                                new Date(l.receivedAt).getTime(),
                            )}
                          </span>
                        </span>
                      )}
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}
      {newTask && (
        <NewTaskModal
          onClose={() => setNewTask(false)}
          about={{
            contactId: contact.id,
            leadId: openLead?.id,
            label: contact.fullName,
          }}
        />
      )}
      {newPolicy && (
        <NewPolicyModal
          onClose={() => setNewPolicy(false)}
          customer={{
            id: contact.id,
            fullName: contact.fullName,
            lifecycleStage: contact.lifecycleStage,
            primaryPhone: primary?.e164 ?? null,
            email: contact.email,
            state: contact.state,
            owner: contact.owner,
            doNotCall: contact.doNotCall,
            updatedAt: contact.updatedAt,
          }}
        />
      )}
    </>
  );
}
